import { el } from './dom.js';
import { audioUrl } from '../audio/soundManifest.js';

const smooth = (t) => {
  const x = Math.min(Math.max(t, 0), 1);
  return x * x * (3 - 2 * x);
};

/**
 * First-run intro — the title card and the black between shots.
 *
 * Owns a full-screen overlay above the UI: an opaque black layer whose opacity
 * this class drives every frame, the typed title lines on top of it, and a
 * faint skip hint. The scene beneath is already the live start-screen preview
 * (built by App behind the loading screen's fade), with CameraManager parked on
 * an {@link IntroCamera} holding its first frame. The timeline is:
 *
 *   black ─ typed card ─ hold ─ text fades ─ gap ─┬─ shot 1 fades up
 *                                         onReveal ┘   (camera + ambient start)
 *   … dips through black at each cut … last shot settles ─ onFinish
 *
 * and App does the rest from `onFinish` — the glide into the cockpit as FT-101
 * begins. Everything runs off the `dt` App hands to `update`, so the card's
 * typing and the camera's shots share one clock. The cut dips are read off the
 * camera's own shot clock rather than kept here, so they can never drift from
 * the cuts they hide.
 *
 * Esc skips — deliberately the only way out, so a stray click can't throw away
 * the intro on a first run. The black lifts over `skipFade` and `onFinish` fires at
 * once, so the glide runs from wherever the camera is under the lifting black.
 */
export default class IntroSequence {
  constructor({ parent, config, camera, volume = 1, onTyped, onReveal, onFinish }) {
    this.cfg = config;
    this.camera = camera;
    this.onTyped = onTyped;     // the title card's last letter just landed
    this.onReveal = onReveal;
    this.onFinish = onFinish;

    this._time = 0;
    this._revealed = false;
    this._finished = false;
    this._skipFrom = -1;     // black opacity when the skip landed (-1 = not skipped)
    this._skipTime = 0;

    // ── DOM ─────────────────────────────────────────────────────────────────
    this.el = el('div', 'intro-overlay');
    this._black = el('div', 'intro-black');
    this._title = el('div', 'intro-title');
    this._cursor = el('span', 'intro-cursor');
    this._hint = el('div', 'intro-skip', 'ESC · SKIP');
    this.el.append(this._black, this._title, this._hint);

    // Every character is laid out from the start and only made visible as it is
    // typed, so the centred lines hold still instead of re-centring per letter.
    // The script is the (time, span) pair for each of them.
    this._script = [];
    let t = config.leadIn;
    config.title.forEach((line, i) => {
      if (i > 0) t += config.linePause;
      const row = el('div', 'intro-line');
      for (const ch of line) {
        const span = el('span', 'intro-char', ch);
        row.appendChild(span);
        t += config.charInterval * (1 + (Math.random() * 2 - 1) * config.charJitter);
        this._script.push({ t, span, tick: ch.trim() !== '' });
      }
      this._title.appendChild(row);
    });
    this._next = 0;
    this._titleEnd = t + config.hold;
    this._revealAt = this._titleEnd + config.titleFadeOut + config.titleGap;
    // The cursor waits at the head of the first line until typing starts.
    this._title.firstChild?.prepend(this._cursor);

    // Per-character tick: one preloaded element, cloned per play so ticks can
    // overlap, as the menu click does (ui/uiSound.js).
    this._tick = new Audio(audioUrl(config.tick.file));
    this._tick.preload = 'auto';
    this._tickVolume = config.tick.volume * volume;

    this._onKey = (e) => { if (e.code === 'Escape' && !e.repeat) this.skip(); };
    window.addEventListener('keydown', this._onKey);

    parent.appendChild(this.el);
  }

  /** Jump straight to the handoff (Esc). */
  skip() {
    if (this._finished) return;
    this._skipFrom = Number(this._black.style.opacity || 1);
    this._skipTime = 0;
    this._title.classList.add('is-gone');
    this._finish(true);
  }

  update(dt) {
    if (this._skipFrom >= 0) {
      // Skipped: lift whatever black is up, then get out of the way.
      this._skipTime += dt;
      const k = smooth(this._skipTime / this.cfg.skipFade);
      this._black.style.opacity = String(this._skipFrom * (1 - k));
      if (k >= 1) this.dispose();
      return;
    }
    if (this._finished) return;

    this._time += dt;
    const t = this._time;
    const cfg = this.cfg;

    // Typing.
    while (this._next < this._script.length && this._script[this._next].t <= t) {
      const c = this._script[this._next++];
      c.span.classList.add('is-typed');
      c.span.after(this._cursor);
      if (c.tick) this._playTick();
      if (this._next === this._script.length) this.onTyped?.();
    }
    if (t >= this._titleEnd) this._title.classList.add('is-gone');
    if (t >= cfg.skipHintDelay) this._hint.classList.add('is-shown');

    if (!this._revealed) {
      if (t < this._revealAt) { this._black.style.opacity = '1'; return; }
      this._revealed = true;
      this.camera.play();
      this.onReveal?.();
    }

    // Picture up: black is a function of where the camera is in its shots.
    const cam = this.camera;
    const shot = cam.shot;
    const fadeIn = cam.index === 0 ? cfg.revealFade : cfg.cutFade;
    let black = 1 - smooth(cam.time / fadeIn);
    if (!cam.isLastShot) black = Math.max(black, 1 - smooth((shot.duration - cam.time) / cfg.cutFade));
    this._black.style.opacity = String(black);

    if (cam.finished) {
      this._black.style.opacity = '0';
      this._finish(false);
      this.dispose();
    }
  }

  _finish(skipped) {
    this._finished = true;
    this._hint.classList.remove('is-shown');
    window.removeEventListener('keydown', this._onKey);
    // A skip ahead of the reveal still owes App its reveal (camera, audio).
    if (!this._revealed) { this._revealed = true; this.camera.play(); this.onReveal?.(); }
    this.onFinish?.({ skipped });
  }

  _playTick() {
    const v = this._tick.cloneNode();
    v.volume = this._tickVolume;
    v.preservesPitch = false;
    v.playbackRate = 1 + (Math.random() * 2 - 1) * this.cfg.tick.pitchJitter;
    v.play().catch(() => { /* interrupted / blocked — ignore */ });
  }

  get done() { return !this.el.isConnected; }

  dispose() {
    window.removeEventListener('keydown', this._onKey);
    this.el.remove();
  }
}
